import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Map screen names (from previous react-navigation setup) to path patterns
const routeNameToPath = {
  DashBoardScreen: '/dashboard',
  LoginScreen: '/login-screen',
  RegisterScreen: '/register-screen', // Base path, paramRefercode is optional
  OtpScreen: '/otp-screen',
  ProductListScreen: '/products-screen',
  ProductDetailsScreen: '/product-details-screen/:paramId/:isWallet',
  UpdateUserInfoScreen: '/update-user-info',
  OrderListScreen: '/orders',
  CartScreen: '/cart-screen',
  MyAccountScreen: '/my-account',
  ShippingAddressScreen: '/shipping-address',
  AddShippingAddressScreen: '/add-shipping-address',
  SubscribeListScreen: '/subscriptionlist-screen',
  CheckoutScreen: '/checkout',
  FaqScreen: '/faq',
  StoriesScreen: '/stories',
  CommonScreen: '/common/:paramInfoId',
  ContactScreen: '/contact-screen',
  InquiryScreen: '/inquiry-screen',
  WalletScreen: '/wallet-screen',
  ThankyouScreen: '/thank-you',
  CancelSubscriptionListScreen: '/cancelled-subscriptions',
  LogSubscriptionListScreen: '/subscription-logs',
  ChangePasswordScreen: '/change-password',
  ForgotPasswordScreen: '/forgot-password',
};

function buildPathFromName(name, params = {}) {
  const pattern = routeNameToPath[name];
  if (!pattern) return null;
  let path = pattern;
  Object.keys(params).forEach((key) => {
    path = path.replace(':' + key, encodeURIComponent(params[key]));
  });
  // remove any unreplaced optional-like segments (keep as-is if not provided)
  return path;
}

export function withNavigation(Component) {
  return function NavigationWrapper(props) {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    const navigation = {
      navigate: (to, options) => {
        try {
          console.log('Navigation.navigate called:', { to, options, pattern: routeNameToPath[to] });
          if (typeof to === 'string') {
            // Support both direct paths and screen-name based navigation with params
            const pattern = routeNameToPath[to];
            if (pattern) {
              // Screen name found - check if it has path parameters
              const hasPathParams = pattern.includes(':');
              if (hasPathParams) {
                // Has path params - extract params from options for path building
                const params = options?.params || (options && typeof options === 'object' && !Array.isArray(options) ? options : {});
                const path = buildPathFromName(to, params);
                const state = options?.params || (options && typeof options === 'object' && !Array.isArray(options) ? options : undefined);
                console.log('Navigating with path params:', { path, state });
                if (state) {
                  navigate(path, { state: state });
                } else {
                  navigate(path);
                }
              } else {
                // Special handling for RegisterScreen - if paramRefercode is provided, append it to path
                if (to === 'RegisterScreen') {
                  const params = options?.params || (options && typeof options === 'object' && !Array.isArray(options) ? options : {});
                  const refCode = params?.paramRefercode;
                  let finalPath = pattern;
                  if (refCode && refCode !== ":paramRefercode" && refCode !== "undefined" && refCode !== "null" && refCode.trim().length > 0) {
                    finalPath = `${pattern}/${encodeURIComponent(refCode)}`;
                  }
                  const state = options?.params || (options && typeof options === 'object' && !Array.isArray(options) ? options : undefined);
                  console.log('Navigating RegisterScreen:', { finalPath, state });
                  if (state && Object.keys(state).length > 0) {
                    navigate(finalPath, { state: state });
                  } else {
                    navigate(finalPath);
                  }
                } else {
                  // No path params - just navigate to the path with state
                  const state = options?.params || (options && typeof options === 'object' && !Array.isArray(options) ? options : undefined);
                  console.log('Navigating without path params:', { pattern, state, willNavigate: true });
                  if (state && Object.keys(state).length > 0) {
                    navigate(pattern, { state: state });
                  } else {
                    console.log('Calling navigate with pattern only:', pattern);
                    navigate(pattern);
                  }
                }
              }
            } else {
              // Not a known screen name, treat as direct path
              const state = options?.params || (options && typeof options === 'object' && !Array.isArray(options) ? options : undefined);
              console.log('Direct path navigation:', { to, state });
              if (state && Object.keys(state).length > 0) {
                navigate(to, { state: state });
              } else {
                navigate(to);
              }
            }
          } else if (to && typeof to === 'object' && to.name) {
            const path = buildPathFromName(to.name, to.params || {});
            if (path) {
              console.log('Object navigation:', { path, state: to.params });
              if (to.params && Object.keys(to.params).length > 0) {
                navigate(path, { state: to.params });
              } else {
                navigate(path);
              }
            }
          }
        } catch (error) {
          console.error('Navigation error:', error);
        }
      },
      replace: (to, options) => {
        const pathFromName = typeof to === 'string' ? buildPathFromName(to, options?.params || options) : null;
        const path = pathFromName || (typeof to === 'string' ? to : '/');
        navigate(path, { replace: true, state: options?.params || options });
      },
      goBack: () => navigate(-1),
    };

    const route = {
      params: { ...(location.state || {}), ...(params || {}) },
    };

    return <Component {...props} navigation={navigation} route={route} />;
  };
}

// Replacement for useIsFocused to mimic react-navigation's focus behavior on web
export function useIsFocused() {
  const location = useLocation();
  const [key, setKey] = React.useState(location.pathname + location.search);
  React.useEffect(() => {
    setKey(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return key; // truthy value that changes on route change
}


